<script lang="ts">
	import type { ISbStoryData } from '@storyblok/js';
	import { editable } from '../editable';
	import PageGrid16 from '../components/pageGrid16.svelte';
	import { innerWidth } from 'svelte/reactivity/window';

	interface DynamicStatsItem {
		percentage: string;
		headline: string;
		subheadline: string;
	}

	interface Props {
		content: ISbStoryData<{
			eyebrow?: string;
			headline: string;
			items: Array<DynamicStatsItem>;
		}>['content'];
	}

	let { content }: Props = $props();

	let isInView = $state(false);
	let sectionElement: HTMLElement;

	let threshold = $derived(innerWidth.current && innerWidth.current >= 1024 ? 0.85 : 0.25);

	$effect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					isInView = true;
					observer.disconnect();
				}
			},
			{ threshold }
		);

		observer.observe(sectionElement);

		return () => {
			observer.disconnect();
		};
	});
</script>

<section
	bind:this={sectionElement}
	use:editable={content}
	class="bg-[#150801] py-17 lg:flex lg:flex-col lg:py-22"
>
	<PageGrid16 margin class="lg:flex-1" gridClass="lg:h-full">
		<div class="col-span-full mb-13 lg:col-start-2 lg:col-end-9 lg:mb-1">
			<div
				class="mb-4 text-[20px]/[140%] tracking-[-.2px] text-stone-2 lg:text-[24px]/[140%] lg:tracking-[-.24px]"
			>
				{content.eyebrow}
			</div>
			<h1
				class="text-balance font-rhymes text-[48px]/[110%] tracking-[-1.44px] text-stone-5 lg:text-[64px]/[110%] lg:tracking-[-.64px]"
			>
				{content.headline}
			</h1>
		</div>

		<div
			class={[
				'col-span-full flex h-full gap-5 max-lg:flex-col lg:col-start-2 lg:col-end-[16] lg:gap-12',
				{ 'animate-bars': isInView }
			]}
		>
			{#each content.items as item, index}
				<div
					class="stat-container lg:flex lg:h-[440px] lg:flex-1 lg:flex-col lg:justify-end"
					style="--stat-value: {item.percentage}%; --stat-delay: {index *
						860}ms; --delay-factor: {parseInt(item.percentage, 10) * 0.01}"
				>
					<div
						class="overflow-hidden rounded-xl bg-[#31251D] max-lg:p-5 lg:flex lg:flex-col lg:justify-between lg:px-7"
					>
						<div>
							<div class="font-rhymes text-[56px]/[120%] text-orange-5 lg:text-[64px]/[120%]">
								{item.percentage}%
							</div>
							<div
								class="mb-6 text-[20px]/[140%] tracking-[-.2px] text-stone-5 lg:text-[24px]/[140%] lg:tracking-[-.24px]"
							>
								{item.headline}
							</div>
						</div>
						<div class="text-pretty text-[14px]/[160%] text-stone-3 lg:text-[16px]/[150%]">
							{item.subheadline}
						</div>
					</div>
				</div>
			{/each}
		</div>
	</PageGrid16>
</section>

<style lang="scss">
	@keyframes grow {
		from {
			height: 0;
			padding-top: 0;
			padding-bottom: 0;
		}
		to {
			height: var(--stat-value);
			padding-top: theme('spacing.7');
			padding-bottom: theme('spacing.7');
		}
	}

	@keyframes slide-in {
		from {
			opacity: 0;
			transform: translateY(64px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}

	.stat-container > div {
		position: relative;
		isolation: isolate;

		&::before {
			content: '';
			position: absolute;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
			z-index: -1;

			background-repeat: repeat;
			opacity: 0.3;
			background-color: lightgray;

			background: image-set(
				url('/textures/dynamic-stat-mobile.webp') type('image/webp'),
				url('/textures/dynamic-stat-mobile.png') type('image/png')
			);

			@media (min-width: 1024px) {
				opacity: 0.12;

				background: image-set(
					url('/textures/dynamic-stat.webp') type('image/webp'),
					url('/textures/dynamic-stat.png') type('image/png')
				);
			}
		}

		@media (min-width: 1024px) {
			height: 0;
		}
	}

	.animate-bars .stat-container {
		@media (max-width: 1023px) {
			opacity: 0;
			animation: slide-in 400ms forwards ease-in-out var(--stat-delay);
		}

		& > div {
			@media (min-width: 1024px) {
				animation: grow 460ms forwards cubic-bezier(0.65, 0, 0.35, 1) var(--stat-delay);

				& > div:first-child {
					& > div:first-child {
						opacity: 0;
						animation: slide-in 600ms forwards cubic-bezier(0.65, 0, 0.35, 1)
							calc(var(--stat-delay) * 1.1);
					}

					& > div:last-child {
						opacity: 0;
						animation: slide-in 460ms forwards cubic-bezier(0.65, 0, 0.35, 1)
							calc(var(--stat-delay) * 1.15);
					}
				}

				& > div:last-child {
					opacity: 0;
					animation: slide-in 460ms forwards cubic-bezier(0.65, 0, 0.35, 1)
						calc(var(--stat-delay) * 1.15 * var(--delay-factor));
				}
			}
		}
	}
</style>
